import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Users, Download, Search, FileSpreadsheet, Send, MessageCircle } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';
import { fmtDate } from '../lib/dateUtils';

const CLASSE_CLR = { A: 'bg-green-600', B: 'bg-green-500', C: 'bg-blue-500', D: 'bg-amber-500', E: 'bg-orange-500', F: 'bg-red-600' };
const STATI = ['inviata', 'da_esaminare', 'preselezionata', 'colloquio_programmato', 'colloquio_effettuato', 'prova_tecnica_richiesta', 'prova_tecnica_superata', 'verifiche_preassuntive', 'idoneo', 'non_idoneo', 'proposta_inviata', 'assunto', 'archiviato'];
const SLOT_LABELS = { lun_mat: 'Lun mattina', lun_pom: 'Lun pom.', mar_mat: 'Mar mattina', mar_pom: 'Mar pom.', mer_mat: 'Mer mattina', mer_pom: 'Mer pom.', gio_mat: 'Gio mattina', gio_pom: 'Gio pom.', ven_mat: 'Ven mattina', ven_pom: 'Ven pom.' };
const waLink = (r) => {
  if (!r.phone) return null;
  let digits = r.phone.replace(/\D/g, '');
  if (digits.startsWith('0039')) digits = digits.slice(2);
  if (!digits.startsWith('39')) digits = '39' + digits.replace(/^0+/, '');
  const link = `${window.location.origin}/disponibilita/${r.token}`;
  const firstName = (r.name || '').split(' ')[0];
  const text = encodeURIComponent(`Ciao ${firstName}, grazie per la tua candidatura presso Metal Covering. Ci dici se sei ancora interessato/a e quando sei disponibile per un breve colloquio? Basta un minuto: ${link}`);
  return `https://wa.me/${digits}?text=${text}`;
};

export default function RecruitmentPage() {
  const [dash, setDash] = useState(null);
  const [positions, setPositions] = useState([]);
  const [rows, setRows] = useState([]);
  const [fPos, setFPos] = useState('all');
  const [fClass, setFClass] = useState('all');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    const params = {};
    if (fPos !== 'all') params.position_id = fPos;
    if (fClass !== 'all') params.classe = fClass;
    if (q) params.q = q;
    const [d, p, r] = await Promise.all([
      api.get('/recruitment/dashboard'),
      api.get('/recruitment/positions'),
      api.get('/recruitment/applications', { params }),
    ]);
    setDash(d.data); setPositions(p.data); setRows(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fPos, fClass]);

  const openDetail = async (id) => { const r = await api.get(`/recruitment/applications/${id}`); setDetail(r.data); };
  const changeStatus = async (status) => {
    let reason = '';
    if (['non_idoneo', 'archiviato'].includes(status)) { reason = prompt('Motivazione (obbligatoria):') || ''; if (!reason) return; }
    await api.post(`/recruitment/applications/${detail.application.id}/status`, { status, reason });
    openDetail(detail.application.id); load();
  };
  const addNote = async () => { if (!note.trim()) return; await api.post(`/recruitment/applications/${detail.application.id}/notes`, { text: note }); setNote(''); openDetail(detail.application.id); };
  const convert = async () => { if (!window.confirm('Convertire il candidato in dipendente del gestionale?')) return; const r = await api.post(`/recruitment/applications/${detail.application.id}/convert-to-employee`, {}); if (r.data.ok) { alert('Candidato convertito in dipendente.'); openDetail(detail.application.id); load(); } };
  const setMedical = async (v) => { await api.post(`/recruitment/applications/${detail.application.id}/medical`, { medical_judgment: v }); openDetail(detail.application.id); };
  const scheduleCall = async () => { const dt = prompt('Data/ora video-call (es. 2026-09-10 15:00):'); if (!dt) return; const link = prompt('Link video-call (opzionale):') || ''; await api.post(`/recruitment/applications/${detail.application.id}/interview`, { type: 'video-call', when: dt, link }); alert('Video-call programmata.'); openDetail(detail.application.id); };
  const doExport = async (fmt) => {
    try {
      const params = {};
      if (fPos !== 'all') params.position_id = fPos;
      if (fClass !== 'all') params.classe = fClass;
      if (q) params.q = q;
      const res = await api.get(`/recruitment/export/applications.${fmt}`, { params, responseType: 'blob' });
      const type = fmt === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv';
      const blob = new Blob([res.data], { type });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `candidature_${new Date().toISOString().split('T')[0]}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      alert('Errore durante l\'esportazione');
    }
  };

  const toggleSelect = (candidateId) => {
    setSelected((s) => { const n = new Set(s); n.has(candidateId) ? n.delete(candidateId) : n.add(candidateId); return n; });
  };

  const sendAvailabilityInvite = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Inviare il form disponibilità via email a ${ids.length} candidat${ids.length === 1 ? 'o' : 'i'}?`)) return;
    try {
      const r = await api.post('/recruitment/candidates/availability-invite', { candidate_ids: ids });
      alert(`Inviate ${r.data.sent} email su ${r.data.total}.` + (r.data.failed.length ? ` ${r.data.failed.length} non inviate (email mancante o errore).` : ''));
      setSelected(new Set());
      load();
    } catch (e) {
      alert("Errore durante l'invio degli inviti");
    }
  };

  const c = detail?.candidate; const a = detail?.application; const sc = a?.score;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2"><Users size={24} className="text-[#1a4b8c]" /><div><h1 className="text-xl font-bold text-slate-800">Campagna Assunzioni</h1><p className="text-sm text-slate-500">Graduatoria e gestione candidature</p></div></div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button size="sm" onClick={sendAvailabilityInvite} className="bg-[#1a4b8c] hover:bg-[#153d70] text-white" data-testid="send-availability-invite">
              <Send size={15} className="mr-1" /> Invia form disponibilità ({selected.size})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => doExport('csv')} data-testid="export-csv"><Download size={15} className="mr-1" /> Esporta CSV</Button>
          <Button size="sm" onClick={() => doExport('xlsx')} className="bg-[#1a7d3c] hover:bg-[#166332] text-white" data-testid="export-xlsx"><FileSpreadsheet size={15} className="mr-1" /> Esporta Excel</Button>
        </div>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="recruit-dash">
          {[['Totali', dash.total], ['Visite pagina', dash.views], ['Candidati/Skip', `${dash.submitted}/${dash.skipped}`], ['Bozze', dash.incomplete], ['Classe A', dash.class_A], ['Assunti', dash.hired]].map(([l, v]) => (
            <Card key={l}><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">{l}</p><p className="text-2xl font-bold text-slate-800">{v}</p></CardContent></Card>
          ))}
        </div>
      )}
      {dash?.by_province && Object.keys(dash.by_province).length > 0 && (
        <Card><CardContent className="p-3">
          <p className="text-xs font-semibold text-slate-600 mb-2">Provenienza geografica</p>
          <div className="flex flex-wrap gap-2">{Object.entries(dash.by_province).sort((a,b)=>b[1]-a[1]).map(([p, n]) => <Badge key={p} variant="outline">{p}: {n}</Badge>)}</div>
        </CardContent></Card>
      )}

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px]"><Search size={16} className="absolute left-2.5 top-2.5 text-slate-400" /><Input className="pl-8" placeholder="Cerca nome/città..." value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} data-testid="recruit-search" /></div>
        <Select value={fPos} onValueChange={setFPos}><SelectTrigger className="w-56" data-testid="filter-position"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tutte le posizioni</SelectItem>{positions.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent></Select>
        <Select value={fClass} onValueChange={setFClass}><SelectTrigger className="w-40" data-testid="filter-class"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tutte le classi</SelectItem>{['A', 'B', 'C', 'D', 'E', 'F'].map(x => <SelectItem key={x} value={x}>Classe {x}</SelectItem>)}</SelectContent></Select>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead className="w-8"></TableHead><TableHead>#</TableHead><TableHead>Classe</TableHead><TableHead>Punt.</TableHead><TableHead>Candidato</TableHead><TableHead>Posizione</TableHead><TableHead className="hidden md:table-cell">Città</TableHead><TableHead>Stato</TableHead><TableHead>Disponibilità</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">Nessuna candidatura</TableCell></TableRow> :
              rows.map(r => {
                const wa = waLink(r);
                const resp = r.availability_response;
                return (
                <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(r.id)} data-testid={`app-row-${r.id}`}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.has(r.candidate_id)} onCheckedChange={() => toggleSelect(r.candidate_id)} data-testid={`select-${r.candidate_id}`} />
                  </TableCell>
                  <TableCell className="font-medium">{r.rank}</TableCell>
                  <TableCell>{r.classe ? <Badge className={CLASSE_CLR[r.classe]}>{r.classe}</Badge> : <span className="text-slate-300">-</span>}</TableCell>
                  <TableCell className="font-semibold">{r.score ?? '-'}</TableCell>
                  <TableCell>{r.name}{r.mode === 'semplice' && <Badge variant="outline" className="ml-2 text-[10px] text-amber-700 border-amber-300" data-testid={`mode-simple-${r.id}`}>semplice</Badge>}</TableCell>
                  <TableCell className="text-sm">{r.position_title}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{r.city} {r.province && `(${r.province})`}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{(r.status || '').replace(/_/g, ' ')}</Badge></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 flex-wrap">
                      {!r.availability_survey_sent_at && !resp && <span className="text-slate-300 text-xs">-</span>}
                      {r.availability_survey_sent_at && !resp && <Badge className="bg-slate-400 text-[10px]">Inviato</Badge>}
                      {resp && resp.interested === true && (
                        <div className="text-[11px]">
                          <Badge className="bg-green-600 text-[10px] mb-0.5">Interessato</Badge>
                          <p className="text-slate-500">{resp.call_type === 'video' ? 'Video' : 'Tel.'} · {resp.contact_pref}</p>
                          <p className="text-slate-500">{(resp.slots || []).map(s => SLOT_LABELS[s] || s).join(', ')}</p>
                        </div>
                      )}
                      {resp && resp.interested === false && <Badge className="bg-red-500 text-[10px]">Non interessato</Badge>}
                      {wa && <a href={wa} target="_blank" rel="noreferrer" title="Invia link via WhatsApp" data-testid={`wa-${r.candidate_id}`}><MessageCircle size={16} className="text-green-600 hover:text-green-700" /></a>}
                    </div>
                  </TableCell>
                </TableRow>
              );})}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          {detail && (
            <div className="space-y-4">
              <DialogHeader><DialogTitle>{c.first_name} {c.last_name} · {a.position_title}{a.mode === 'semplice' && <Badge variant="outline" className="ml-2 text-xs text-amber-700 border-amber-300" data-testid="detail-mode-simple">candidatura semplice</Badge>}</DialogTitle></DialogHeader>
              <div className="grid md:grid-cols-3 gap-3 text-sm">
                <div className="md:col-span-2 space-y-1">
                  <p><b>Email:</b> {c.email} · <b>Tel:</b> {c.phone || '-'}</p>
                  <p><b>Residenza:</b> {c.city} {c.province && `(${c.province})`} · <b>Patente:</b> {c.license || '-'} · <b>Auto:</b> {c.has_car ? 'sì' : 'no'}</p>
                  <p><b>Disponibile dal:</b> {c.availability_date ? fmtDate(c.availability_date) : '-'}</p>
                  {c.cv_url && <a className="text-blue-600 underline" href={process.env.REACT_APP_BACKEND_URL + c.cv_url} target="_blank" rel="noreferrer">Scarica CV</a>}
                </div>
                {sc && <div className="text-center border rounded-lg p-3"><Badge className={CLASSE_CLR[sc.classe] + ' text-base px-3'}>{sc.classe}</Badge><p className="text-3xl font-bold mt-1">{sc.total}</p><p className="text-xs text-slate-400">v{sc.criteria_version}</p></div>}
              </div>

              {sc && (
                <Card className="bg-slate-50"><CardContent className="p-3 text-xs space-y-2">
                  <p className="font-semibold text-sm">Motivazione punteggio</p>
                  <div className="flex flex-wrap gap-1">{Object.entries(sc.per_area).map(([k, v]) => <Badge key={k} variant="outline">{k.replace(/_/g, ' ')}: {v}%</Badge>)}</div>
                  {sc.eliminatorie_fail?.length > 0 && <p className="text-red-600">Requisiti chiave non soddisfatti: {sc.eliminatorie_fail.join('; ')}</p>}
                  {sc.mancanti?.length > 0 && <p className="text-amber-700">Mancanti: {sc.mancanti.join('; ')}</p>}
                  {sc.da_verificare?.length > 0 && <p className="text-slate-500">Da verificare: {sc.da_verificare.slice(0, 5).join('; ')}</p>}
                </CardContent></Card>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Select value={a.status} onValueChange={changeStatus}><SelectTrigger className="w-64" data-testid="status-select"><SelectValue /></SelectTrigger><SelectContent>{STATI.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent></Select>
                {a.status !== 'assunto' && <Button size="sm" onClick={convert} data-testid="convert-btn">Assumi → crea dipendente</Button>}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-sm border-t pt-3">
                <span className="text-slate-600">Disponibilità: <b>{c.work_status || 'n.d.'}</b></span>
                <Button size="sm" variant="outline" onClick={scheduleCall} data-testid="schedule-call">Programma video-call</Button>
                <span className="text-slate-500 ml-2">Giudizio medico:</span>
                <Select value={a.medical_judgment || undefined} onValueChange={setMedical}>
                  <SelectTrigger className="w-52" data-testid="medical-select"><SelectValue placeholder="Non richiesto" /></SelectTrigger>
                  <SelectContent>{['idoneo', 'idoneo con prescrizioni', 'temporaneamente non idoneo', 'non idoneo', 'da verificare'].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(a.interviews || []).length > 0 && <p className="text-xs text-slate-500">Colloqui: {(a.interviews || []).map(i => `${i.type || ''} ${i.when || ''}`).join(' · ')}</p>}
              <p className="text-[11px] text-slate-400">Periodo di prova minimo 30 giorni; formazione e visita medica obbligatorie con clausola di vincolo costi di assunzione, comunicate al candidato prima dell'avvio.</p>

              <div className="space-y-2">
                <p className="font-semibold text-sm">Note interne</p>
                {(a.notes || []).map(n => <div key={n.id} className="text-xs bg-slate-50 rounded p-2"><b>{n.by_name || 'utente'}:</b> {n.text} <span className="text-slate-400">· {(n.at || '').slice(0, 10)}</span></div>)}
                <div className="flex gap-2"><Input value={note} onChange={e => setNote(e.target.value)} placeholder="Aggiungi nota..." data-testid="note-input" /><Button size="sm" onClick={addNote} data-testid="add-note">Aggiungi</Button></div>
              </div>

              <details className="text-xs"><summary className="cursor-pointer font-semibold text-sm">Risposte al questionario</summary>
                <div className="mt-2 space-y-1">{Object.entries(a.answers || {}).map(([k, v]) => <p key={k}><b>{k}:</b> {Array.isArray(v) ? v.join(', ') : String(v)}</p>)}</div>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
